import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Row } from 'antd';

import type { RootState } from '@/redux/RootReducer';

import { ReactComponent as OpenClaimsIcon } from '../../../../assets/icons/cart-late.svg';
import { ReactComponent as AmountPaidClaimsIcon } from '../../../../assets/icons/cart-warning.svg';
import { ReactComponent as DashboardIcon } from '../../../../assets/icons/dashboard-icon.svg';
import { ReactComponent as AmountFiledClaimsIcon } from '../../../../assets/icons/document-warning.svg';
import { ReactComponent as FilesIcon } from '../../../../assets/icons/files-records.svg';
import { ReactComponent as LateDeliveryClaimsIcon } from '../../../../assets/icons/late-delivery.svg';
import { ReactComponent as HoldClaimsIcon } from '../../../../assets/icons/tracking-failed.svg';
import { setClaimsOverChargeFilters } from '../../../../redux/Claims/OverCharge/OverChargeActions';
import { routeFileSelector } from '../../../../utilz/i18n';
import VerticalLine from '../../../components/Bars/VerticalLine';
import IconButton from '../../../components/Buttons/IconButton';
import PrimaryLabel from '../../../components/Labels/PrimaryLabel';
import Drawer from '../../../components/Modals/Drawer';
import FooterContentCard from '../../common/FooterContentCard';
import { CLAIMS_FOOTER_TYPES } from '../constants';
import type { ClaimsFooterType } from '../types';
import OCClaimDashboard from './Dashboard/OCClaimDashboard';
import { useGetOCClaimsList } from './hooks';

const OverChargeClaimsFooter: FC = () => {
  const [visible, setVisible] = useState<boolean>(false);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const {
    claimsFilters: { FooterStatus, ...claimsFilters },
  } = useSelector((state: RootState) => state.overChargeClaims);
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const filters = { FooterStatus, ...claimsFilters };
  const { ...searchFilters } = filters;
  const { data } = useGetOCClaimsList({
    companyId: selectedCompany?.companyId,
    enabled: Boolean(selectedCompany?.companyId),
    searchFilters,
  });
  const footerData = data?.footerData;

  const dispatch = useDispatch();

  const handleFooterClick = (footerStatus: ClaimsFooterType) => {
    dispatch(setClaimsOverChargeFilters({ FooterStatus: footerStatus, pageNo: 1 }));
  };

  return (
    <div className="">
      <Row justify="space-around" className=" footer-container background-header px-20  py-12">
        <Col>
          <FooterContentCard
            count={String(footerData?.totalClaim ?? 0)}
            label={t('translation:claims.totalClaims')}
            Icon={FilesIcon}
            onClick={() => handleFooterClick(CLAIMS_FOOTER_TYPES.ALL)}
            active={CLAIMS_FOOTER_TYPES.ALL === FooterStatus}
          />
        </Col>
        <Col>
          <VerticalLine />
        </Col>
        <Col>
          <FooterContentCard
            count={String(footerData?.openClaim ?? 0)}
            label={t('translation:claims.openClaims')}
            Icon={OpenClaimsIcon}
            onClick={() => handleFooterClick(CLAIMS_FOOTER_TYPES.OPEN)}
            active={CLAIMS_FOOTER_TYPES.OPEN === FooterStatus}
          />
        </Col>

        <Col>
          <VerticalLine />
        </Col>
        <Col>
          <FooterContentCard
            count={String(footerData?.holdClaim ?? 0)}
            label={t('translation:claims.onHoldClaims')}
            Icon={HoldClaimsIcon}
            onClick={() => handleFooterClick(CLAIMS_FOOTER_TYPES.HOLD)}
            active={CLAIMS_FOOTER_TYPES.HOLD === FooterStatus}
          />
        </Col>
        <Col>
          <VerticalLine />
        </Col>
        <Col>
          <FooterContentCard
            count={String(footerData?.closedClaim ?? 0)}
            label={t('translation:claims.closedClaims')}
            Icon={LateDeliveryClaimsIcon}
            // onClick={() => handleFooterClick(CLAIMS_FOOTER_TYPES.CLOSED)}
            // active={CLAIMS_FOOTER_TYPES.CLOSED === FooterStatus}
            color="muted"
            textColor="text-muted"
          />
        </Col>
        <Col>
          <VerticalLine />
        </Col>
        <Col>
          <FooterContentCard
            count={
              <NumericFormat
                thousandsGroupStyle="thousand"
                displayType="text"
                value={String(footerData?.filedAmount ?? 0)}
                thousandSeparator
                fixedDecimalScale
                decimalScale={2}
              />
            }
            label={t('translation:claims.totalAmountFiled')}
            Icon={AmountFiledClaimsIcon}
          />
        </Col>
        <Col>
          <VerticalLine />
        </Col>
        <Col>
          <FooterContentCard
            count={
              <NumericFormat
                thousandsGroupStyle="thousand"
                displayType="text"
                value={String(footerData?.paidAmount ?? 0)}
                thousandSeparator
                fixedDecimalScale
                decimalScale={2}
              />
            }
            label={t('translation:claims.totalAmountPaid')}
            Icon={AmountPaidClaimsIcon}
          />
        </Col>
        <Col>
          <VerticalLine />
        </Col>
        <Col className="d-flex">
          <IconButton onClick={() => setVisible(true)}>
            <DashboardIcon />
          </IconButton>
        </Col>
      </Row>
      <Drawer
        width="90%"
        open={visible}
        headerContents={
          <PrimaryLabel className="text-white">{t('translation:default.navMenu.dashboard')}</PrimaryLabel>
        }
        onClose={() => setVisible(false)}
      >
        <OCClaimDashboard />
      </Drawer>
    </div>
  );
};

export default OverChargeClaimsFooter;
